import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Legend } from 'recharts';
import { CLIMBING_STYLES } from '../../utils/constants.js';
import { getAscentTypeColor, SPORT_CLEAN_ASCENT_TYPES, BOULDER_CLEAN_ASCENT_TYPES } from '../../utils/gradeUtils.js';

const ClimbingPartnersChart = ({ data, climbingStyle, selectedStat, numPartners }) => {
  const processedData = useMemo(() => {
    if (!data?.length) return [];
    
    // Create a map to store partner statistics
    const partnerStats = new Map();
    
    // First pass: Collect all ascents per partner
    data.forEach(ascent => {
      const withField = ascent["Ascent Partners"] || ascent.With;
      if (!withField) return;

      const partners = withField.split(',').map(p => p.trim()).filter(p => p);
      
      partners.forEach(partner => {
        if (!partnerStats.has(partner)) {
          partnerStats.set(partner, {
            name: partner,
            ascents: [],
            attempts: 0,
            totalAscents: 0,
            beginnerAscents: 0,
            intermediateAscents: 0,
            experiencedAscents: 0,
            expertAscents: 0,
            eliteAscents: 0,
            beginnerRoutes: new Set(),
            intermediateRoutes: new Set(),
            experiencedRoutes: new Set(),
            expertRoutes: new Set(),
            eliteRoutes: new Set(),
            onsight: 0,
            flash: 0,
            redpoint: 0,
            pinkpoint: 0,
            send: 0,
            repeat: 0,
            other: 0
          });
        }
        
        const stats = partnerStats.get(partner);
        stats.ascents.push(ascent);
        
        // Update attempts
        stats.attempts++;
        
        // Update ascent types
        if (ascent["Ascent Type"]) {
          const type = ascent["Ascent Type"].toLowerCase().replace(/\s+/g, '');
          if (stats[type] !== undefined) {
            stats[type]++;
          } else {
            stats.other++;
          }
        }
        
        // Update grade categories and unique routes
        if (ascent.isClean) {
          stats.totalAscents++;
          
          // Categorize by grade based on climbing style
          const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? 
            ascent.vgrade : 
            ascent.ewbanks;
          
          const routeId = ascent["Route ID"];
          
          if (climbingStyle === CLIMBING_STYLES.BOULDER) {
            if (grade < -0.5) {
              stats.beginnerAscents++;
              if (routeId) stats.beginnerRoutes.add(routeId);
            }
            else if (grade <= 0.5) {
              stats.intermediateAscents++;
              if (routeId) stats.intermediateRoutes.add(routeId);
            }
            else if (grade <= 4) {
              stats.experiencedAscents++;
              if (routeId) stats.experiencedRoutes.add(routeId);
            }
            else if (grade <= 10) {
              stats.expertAscents++;
              if (routeId) stats.expertRoutes.add(routeId);
            }
            else {
              stats.eliteAscents++;
              if (routeId) stats.eliteRoutes.add(routeId);
            }
          } else {
            if (grade <= 12) {
              stats.beginnerAscents++;
              if (routeId) stats.beginnerRoutes.add(routeId);
            }
            else if (grade <= 18) {
              stats.intermediateAscents++;
              if (routeId) stats.intermediateRoutes.add(routeId);
            }
            else if (grade <= 24) {
              stats.experiencedAscents++;
              if (routeId) stats.experiencedRoutes.add(routeId);
            }
            else if (grade <= 32) {
              stats.expertAscents++;
              if (routeId) stats.expertRoutes.add(routeId);
            }
            else {
              stats.eliteAscents++;
              if (routeId) stats.eliteRoutes.add(routeId);
            }
          }
        }
      });
    });
    
    // Second pass: Calculate CGP for each partner
    const processedStats = Array.from(partnerStats.values()).map(stat => {
      // Calculate CGP
      const cleanAscents = stat.ascents.filter(ascent => {
        const isCleanAscent = climbingStyle === CLIMBING_STYLES.BOULDER ? 
          BOULDER_CLEAN_ASCENT_TYPES.includes(ascent["Ascent Type"]) :
          SPORT_CLEAN_ASCENT_TYPES.includes(ascent["Ascent Type"]);
        
        return isCleanAscent && ascent.isClean;
      });

      let cpg = 0;
      if (cleanAscents.length > 0) {
        if (climbingStyle === CLIMBING_STYLES.BOULDER) {
          const sum = cleanAscents.reduce((acc, ascent) => {
            if (ascent.vgrade === null || isNaN(ascent.vgrade)) return acc;
            return acc + Math.exp(ascent.vgrade);
          }, 0);
          if (sum > 0) {
            cpg = Number(Math.log(sum).toFixed(2));
          }
        } else {
          const sum = cleanAscents.reduce((acc, ascent) => {
            if (ascent.ewbanks === null || isNaN(ascent.ewbanks)) return acc;
            return acc + Math.pow(2, ascent.ewbanks);
          }, 0);
          if (sum > 0) {
            cpg = Number(Math.log2(sum).toFixed(2));
          }
        }
      }

      return {
        name: stat.name,
        cpg,
        attempts: stat.attempts,
        totalAscents: stat.totalAscents,
        beginnerAscents: stat.beginnerAscents,
        intermediateAscents: stat.intermediateAscents,
        experiencedAscents: stat.experiencedAscents,
        expertAscents: stat.expertAscents,
        eliteAscents: stat.eliteAscents,
        beginnerRoutes: stat.beginnerRoutes.size,
        intermediateRoutes: stat.intermediateRoutes.size,
        experiencedRoutes: stat.experiencedRoutes.size,
        expertRoutes: stat.expertRoutes.size,
        eliteRoutes: stat.eliteRoutes.size,
        onsight: stat.onsight,
        flash: stat.flash,
        redpoint: stat.redpoint,
        pinkpoint: stat.pinkpoint,
        send: stat.send,
        repeat: stat.repeat,
        other: stat.other
      };
    });
    
    // Filter and sort based on selected stat
    return processedStats
      .filter(partner => {
        if (selectedStat === 'cleanRoutes') {
          const totalRoutes = (partner.beginnerRoutes || 0) + 
                            (partner.intermediateRoutes || 0) + 
                            (partner.experiencedRoutes || 0) + 
                            (partner.expertRoutes || 0) + 
                            (partner.eliteRoutes || 0);
          return totalRoutes > 0;
        } else if (selectedStat === 'cleanAscents') {
          const totalAscents = (partner.beginnerAscents || 0) + 
                             (partner.intermediateAscents || 0) + 
                             (partner.experiencedAscents || 0) + 
                             (partner.expertAscents || 0) + 
                             (partner.eliteAscents || 0);
          return totalAscents > 0;
        } else if (selectedStat === 'attempts') {
          return partner.attempts > 0;
        } else if (selectedStat === 'cpg') {
          return typeof partner.cpg === 'number' && partner.cpg > 0;
        }
        return true;
      })
      .sort((a, b) => {
        if (selectedStat === 'cleanRoutes') {
          const aTotal = (a.beginnerRoutes || 0) + 
                        (a.intermediateRoutes || 0) + 
                        (a.experiencedRoutes || 0) + 
                        (a.expertRoutes || 0) + 
                        (a.eliteRoutes || 0);
          const bTotal = (b.beginnerRoutes || 0) + 
                        (b.intermediateRoutes || 0) + 
                        (b.experiencedRoutes || 0) + 
                        (b.expertRoutes || 0) + 
                        (b.eliteRoutes || 0);
          return bTotal !== aTotal ? bTotal - aTotal : 
                 (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
        } else if (selectedStat === 'cleanAscents') {
          const aTotal = (a.beginnerAscents || 0) + 
                        (a.intermediateAscents || 0) + 
                        (a.experiencedAscents || 0) + 
                        (a.expertAscents || 0) + 
                        (a.eliteAscents || 0);
          const bTotal = (b.beginnerAscents || 0) + 
                        (b.intermediateAscents || 0) + 
                        (b.experiencedAscents || 0) + 
                        (b.expertAscents || 0) + 
                        (b.eliteAscents || 0);
          return bTotal !== aTotal ? bTotal - aTotal :
                 (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
        } else if (selectedStat === 'attempts') {
          return b.attempts !== a.attempts ? b.attempts - a.attempts :
                 (typeof a.cpg === 'number' && typeof b.cpg === 'number') ? (b.cpg - a.cpg) : 0;
        }
        return (b[selectedStat] || 0) - (a[selectedStat] || 0);
      })
      .slice(0, numPartners);
  }, [data, climbingStyle, selectedStat, numPartners]);

  const activeCategories = useMemo(() => {
    if (!processedData.length) return {};

    const useRoutes = selectedStat === 'cleanRoutes';
    const categories = {
      beginner: processedData.some(partner => (partner[useRoutes ? "beginnerRoutes" : "beginnerAscents"] || 0) > 0),
      intermediate: processedData.some(partner => (partner[useRoutes ? "intermediateRoutes" : "intermediateAscents"] || 0) > 0),
      experienced: processedData.some(partner => (partner[useRoutes ? "experiencedRoutes" : "experiencedAscents"] || 0) > 0),
      expert: processedData.some(partner => (partner[useRoutes ? "expertRoutes" : "expertAscents"] || 0) > 0),
      elite: processedData.some(partner => (partner[useRoutes ? "eliteRoutes" : "eliteAscents"] || 0) > 0),
      onsight: processedData.some(partner => (partner.onsight || 0) > 0),
      flash: processedData.some(partner => (partner.flash || 0) > 0),
      send: processedData.some(partner => (partner.send || 0) > 0),
      repeat: processedData.some(partner => (partner.repeat || 0) > 0),
      redpoint: processedData.some(partner => (partner.redpoint || 0) > 0),
      pinkpoint: processedData.some(partner => (partner.pinkpoint || 0) > 0),
      other: processedData.some(partner => (partner.other || 0) > 0)
    };
    return categories;
  }, [processedData, selectedStat]);

  const getGradeBreakdownBars = () => {
    const useRoutes = selectedStat === 'cleanRoutes';
    
    return (
      <>
        {activeCategories.beginner && (
          <Bar 
            animationDuration={300}
            dataKey={useRoutes ? "beginnerRoutes" : "beginnerAscents"}
            stackId="a"
            fill="#22c55e"  
            name="Beginner"
          />
        )}
        {activeCategories.intermediate && (
          <Bar 
            animationDuration={300}
            dataKey={useRoutes ? "intermediateRoutes" : "intermediateAscents"}
            stackId="a"
            fill="#fbbf24"  
            name="Intermediate"
          />
        )}
        {activeCategories.experienced && (
          <Bar 
            animationDuration={300}
            dataKey={useRoutes ? "experiencedRoutes" : "experiencedAscents"}
            stackId="a"
            fill="#fb923c"
            name="Experienced"
          />
        )}
        {activeCategories.expert && (
          <Bar 
            animationDuration={300}
            dataKey={useRoutes ? "expertRoutes" : "expertAscents"}
            stackId="a"
            fill="#dc2626" 
            name="Expert"
          />
        )}
        {activeCategories.elite && (
          <Bar 
            animationDuration={300}
            dataKey={useRoutes ? "eliteRoutes" : "eliteAscents"}
            stackId="a"
            fill="#a855f7"  
            name="Elite"
          />
        )}
      </>
    );
  };

  const getAscentBars = () => {
    if (climbingStyle === CLIMBING_STYLES.BOULDER) {
      return (
        <>
          {activeCategories.onsight && (
            <Bar
              animationDuration={300}
              dataKey="onsight"
              stackId="a"
              fill={getAscentTypeColor('Onsight')}
              name="Onsight"
            />
          )}
          {activeCategories.flash && (
            <Bar
              animationDuration={300}
              dataKey="flash"
              stackId="a"
              fill={getAscentTypeColor('Flash')}
              name="Flash"
            />
          )}
          {activeCategories.send && (
            <Bar
              animationDuration={300}
              dataKey="send"
              stackId="a"
              fill={getAscentTypeColor('Send')}
              name="Send"
            />
          )}
          {activeCategories.repeat && (
            <Bar
              animationDuration={300}
              dataKey="repeat"
              stackId="a"
              fill={getAscentTypeColor('Repeat')}
              name="Repeat"
            />
          )}
          {activeCategories.other && (
            <Bar
              animationDuration={300}
              dataKey="other"
              stackId="a"
              fill={getAscentTypeColor('Other')}
              name="Attempts"
            />
          )}
        </>
      );
    }
    
    return (
      <>
        {activeCategories.onsight && (
          <Bar
            animationDuration={300}
            dataKey="onsight"
            stackId="a"
            fill={getAscentTypeColor('Onsight')}
            name="Onsight"
          />
        )}
        {activeCategories.flash && (
          <Bar
            animationDuration={300}
            dataKey="flash"
            stackId="a"
            fill={getAscentTypeColor('Flash')}
            name="Flash"
          />
        )}
        {activeCategories.redpoint && (
          <Bar
            animationDuration={300}
            dataKey="redpoint"
            stackId="a"
            fill={getAscentTypeColor('Red point')}
            name="Red point"
          />
        )}
        {activeCategories.pinkpoint && (
          <Bar
            animationDuration={300}
            dataKey="pinkpoint"
            stackId="a"
            fill={getAscentTypeColor('Pink point')}
            name="Pink point"
          />
        )}
        {activeCategories.other && (
          <Bar
            animationDuration={300}
            dataKey="other"
            stackId="a"
            fill={getAscentTypeColor('Other')}
            name="Attempts"
          />
        )}
      </>
    );
  };
  
  const getLegendTitle = () => {
    if (selectedStat === 'attempts') {
      return 'Ascent Type';
    } else if (selectedStat === 'cpg') {
      return null;
    } else {
      return 'Difficulty';
    }
  };
  
  const CustomLegendContent = (props) => {
    const { payload } = props;
    
    const title = getLegendTitle();
    
    return (
      <div style={{ fontSize: '12px' }}>
        {title && (
          <div style={{ 
            borderBottom: '1px solid #d1d5db', 
            marginBottom: '8px', 
            paddingBottom: '4px',
            fontWeight: 500
          }}>
            {title}
          </div>
        )}
        {payload?.map((entry, index) => (
          <div key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <div style={{ 
              width: '10px', 
              height: '10px', 
              backgroundColor: entry.color,
              marginRight: '8px'
            }}/>
            <span>{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };
  
  const statOptions = [
    { value: 'cpg', label: 'Cumulative Grade' },
    { value: 'attempts', label: 'Total Attempts' },
    { value: 'cleanAscents', label: 'Clean Ascents' },
    { value: 'cleanRoutes', label: 'Clean Routes' }
  ];

  return (
    <div className="w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={processedData}
          margin={{ top: 0, right: 0, left: 0, bottom: 20 }}
          layout="vertical"
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            type="number"
            domain={[selectedStat === 'cpg' ? 'auto' : 0, 'auto']}
            label={{ 
              value: statOptions.find(opt => opt.value === selectedStat)?.label,
              position: "bottom",
              offset: 0
            }}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            width={160}
            interval={0}
            tick={({ x, y, payload }) => (
              <text
                x={x}
                y={y}
                dy={3}
                textAnchor="end"
                fontSize={numPartners > 10 ? 10 : 12}
                fill="#666"
              >
                {payload.value}
              </text>
            )}
          />
          <Tooltip
            formatter={(value, name) => [value || 0, name]}
          />
          <Legend 
            content={<CustomLegendContent />}
            layout="vertical"
            align="center"
            verticalAlign="middle"
            wrapperStyle={{
              fontSize: '12px',
              padding: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              position: 'absolute',
              bottom: '55px',
              right: '5px',
              transform: 'translateY(0)',
              zIndex: 100
            }}
          />
          
          {selectedStat === 'attempts' ? (
            getAscentBars()
          ) : selectedStat === 'cpg' ? (
            <Bar 
              animationDuration={300}
              dataKey={selectedStat}
              fill="#3B82F6"
              name={statOptions.find(opt => opt.value === selectedStat)?.label}
            />
          ) : (
            getGradeBreakdownBars()
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ClimbingPartnersChart;